import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Search } from '@material-ui/icons';
import {
  CircularProgress,
  InputAdornment,
  MenuItem,
  TextField,
} from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import ApprovalBookingCard from './ApprovalBookingCard';
import { getBookings } from '../../services/apiService';
import { getFullName } from '../../utils';
import { BOOKING_STAGES } from '../../constants';

class SessionApproval extends Component {
  constructor(props) {
    super(props);
    this.state = {
      bookings: [],
      bookingCounts: {},
      selectedTheme: 'all',
      filter: {
        searchString: '',
        status: BOOKING_STAGES.FEEDBACK_SUBMITTED.value,
        subTopic: '',
        candidate: '',
        coach: '',
        fromDate: null,
        toDate: null,
      },
      filterOptions: {
        subTopics: [],
        candidates: [],
        coaches: [],
      },
      loading: true,
    };
  }

  componentDidMount = async () => {
    const filterObj = {
      stage: BOOKING_STAGES.FEEDBACK_SUBMITTED.value,
    };
    const bookings = await getBookings(filterObj);
    const bookingCounts = { all: bookings.length };
    const subTopics = new Set();
    const candidates = new Set();
    const coaches = new Set();
    bookings.forEach((b) => {
      bookingCounts[b.themeId] = (bookingCounts[b.themeId] || 0) + 1;
      subTopics.add(b.subTopicName);
      candidates.add([b.Student?.firstName, b.Student?.lastName].join(' '));
      coaches.add([b.Coach?.firstName, b.Coach?.lastName].join(' '));
    });
    this.setState({
      bookings,
      bookingCounts,
      filterOptions: {
        subTopics: Array.from(subTopics),
        candidates: Array.from(candidates),
        coaches: Array.from(coaches),
      },
      loading: false,
    });
  };

  refreshBookings = async () => {
    this.setState({ loading: true });
    const filterObj = {
      stage: this.state.filter.status,
    };
    const bookings = await getBookings(filterObj);
    this.setState({ bookings, loading: false });
  };

  componentDidUpdate = async (prevProps, prevStates) => {
    if (this.state.filter.status !== prevStates.filter.status)
      await this.refreshBookings();
  };

  render() {
    // filter booking list wtr to selected filters
    let filteredBookings = this.state.bookings;
    if (this.state.filter.candidate)
      filteredBookings = filteredBookings.filter(
        (b) => getFullName(b.Student) === this.state.filter.candidate
      );
    if (this.state.filter.coach)
      filteredBookings = filteredBookings.filter(
        (b) => getFullName(b.Coach) === this.state.filter.coach
      );
    if (this.state.filter.subTopic)
      filteredBookings = filteredBookings.filter(
        (b) => b.subTopicName === this.state.filter.subTopic
      );
    if (this.state.filter.fromDate)
      filteredBookings = filteredBookings.filter(
        (b) => new Date(b.startDate) >= new Date(this.state.filter.fromDate)
      );
    if (this.state.filter.toDate)
      filteredBookings = filteredBookings.filter(
        (b) => new Date(b.endDate) <= new Date(this.state.filter.toDate)
      );
    if (this.state.filter.status)
      filteredBookings = filteredBookings.filter(
        (b) => b.stage === this.state.filter.status
      );
    if (this.state.filter.searchString.length > 2) {
      const ss = this.state.filter.searchString.toLocaleLowerCase();
      filteredBookings = filteredBookings.filter(
        (b) =>
          b.themeName.toLowerCase().includes(ss) ||
          b.subTopicName.toLowerCase().includes(ss) ||
          getFullName(b.Student).toLowerCase().includes(ss) ||
          getFullName(b.Coach).toLowerCase().includes(ss)
      );
    }

    return (
      <div>
        {/* header */}
        <div className='flex justify-between'>
          <h1 className='text-3xl font-semibold opacity-80'>
            Session Approval
          </h1>
          <TextField
            onChange={({ target }) =>
              this.setState({
                filter: {
                  ...this.state.filter,
                  searchString: target.value,
                },
              })
            }
            placeholder='Search Bookings'
            value={this.state.filter.searchString}
            variant='outlined'
            size='small'
            className='bg-white rounded w-full max-w-sm'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start' className='opacity-60'>
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className='mt-8 flex items-center justify-between gap-8'>
          {/* theme selection bar */}
          <div className='flex items-center gap-4 whitespace-nowrap overflow-x-auto hidden-scrollbar'>
            <span
              onClick={() => this.setState({ selectedTheme: 'all' })}
              className={
                this.state.selectedTheme === 'all'
                  ? 'text-blue-c1 font-medium'
                  : 'cursor-pointer opacity-40'
              }
            >
              All ({this.state.bookingCounts.all || 0})
            </span>
            {this.props?.allThemes?.map((theme) => (
              <span
                onClick={() => this.setState({ selectedTheme: theme.id })}
                key={theme.id}
                className={
                  'border-l border-gray-400 pl-4 ' +
                  (theme.id === this.state.selectedTheme
                    ? 'text-blue-c1 font-medium'
                    : 'cursor-pointer opacity-40')
                }
              >
                {theme.name} ({this.state.bookingCounts[theme.id] || 0})
              </span>
            ))}
          </div>

          {/* status filter */}
          <TextField
            select
            onChange={({ target }) =>
              this.setState({
                filter: {
                  ...this.state.filter,
                  status: target.value,
                },
              })
            }
            value={this.state.filter.status}
            label='Status'
            variant='outlined'
            size='small'
            className='float-right bg-white rounded w-32'
          >
            <MenuItem value={BOOKING_STAGES.FEEDBACK_SUBMITTED.value}>
              New
            </MenuItem>
            <MenuItem value={BOOKING_STAGES.ADMIN_ON_HOLD.value}>Hold</MenuItem>
          </TextField>
        </div>

        {/* filter options bar */}
        <div className='grid grid-cols-9 divide-x divide-black divide-solid divide-opacity-20 bg-white my-4'>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <div className='col-span-3 p-2 flex gap-2'>
              <KeyboardDatePicker
                format='MM/dd/yy'
                onChange={(date) =>
                  this.setState({
                    filter: {
                      ...this.state.filter,
                      fromDate: date,
                    },
                  })
                }
                value={this.state.filter.fromDate}
                label='From'
                inputVariant='outlined'
                size='small'
                clearable
                fullWidth
              />
              <KeyboardDatePicker
                format='MM/dd/yyyy'
                onChange={(date) =>
                  this.setState({
                    filter: {
                      ...this.state.filter,
                      toDate: date,
                    },
                  })
                }
                value={this.state.filter.toDate}
                label='To'
                inputVariant='outlined'
                size='small'
                clearable
                fullWidth
              />
            </div>
          </MuiPickersUtilsProvider>
          <div className='col-span-2 p-2'>
            <TextField
              select
              onChange={({ target }) =>
                this.setState({
                  filter: {
                    ...this.state.filter,
                    subTopic: target.value,
                  },
                })
              }
              value={this.state.filter.subTopic}
              label='Sub Topic'
              variant='outlined'
              size='small'
              className='float-right bg-white rounded'
              fullWidth
            >
              <MenuItem value=''>
                <i>All</i>
              </MenuItem>
              {this.state.filterOptions.subTopics.map((subTopic) => (
                <MenuItem value={subTopic} key={subTopic}>
                  {subTopic}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className='col-span-2 p-2'>
            <TextField
              select
              onChange={({ target }) =>
                this.setState({
                  filter: {
                    ...this.state.filter,
                    candidate: target.value,
                  },
                })
              }
              value={this.state.filter.candidate}
              label='Candidate'
              variant='outlined'
              size='small'
              className='float-right bg-white rounded'
              fullWidth
            >
              <MenuItem value=''>
                <i>All</i>
              </MenuItem>
              {this.state.filterOptions.candidates.map((candidate) => (
                <MenuItem value={candidate} key={candidate}>
                  {candidate}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className='col-span-2 p-2'>
            <TextField
              select
              onChange={({ target }) =>
                this.setState({
                  filter: {
                    ...this.state.filter,
                    coach: target.value,
                  },
                })
              }
              value={this.state.filter.coach}
              label='Coach'
              variant='outlined'
              size='small'
              className='float-right bg-white rounded'
              fullWidth
            >
              <MenuItem value=''>
                <i>All</i>
              </MenuItem>
              {this.state.filterOptions.coaches.map((coach) => (
                <MenuItem value={coach} key={coach}>
                  {coach}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>
        {!this.state.loading ? (
          <div className='flex flex-col gap-4'>
            {filteredBookings.map((booking, index) => (
              <ApprovalBookingCard bookingData={booking} key={index} />
            ))}
          </div>
        ) : (
          <div className='text-center text-blue-c1 mt-32'>
            <CircularProgress color='inherit' />
          </div>
        )}
        {!this.state.loading && filteredBookings.length === 0 && (
          <center>
            <h1 className='mt-32 font-semibold text-2xl opacity-30'>
              No Bookings Found
            </h1>
          </center>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  allThemes: state.themeReducer.themes,
});
export default connect(mapStateToProps, null)(SessionApproval);
